import React, { useState } from 'react';
import { DashboardTemplate } from '../components/templates/DashboardTemplate.tsx';
import {
  Box,
  VStack,
  Text,
  Button,
  useToast,
  Heading,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { CPFInput } from '@partiu/ui/components/atoms/Input.tsx';
import { FaFlask } from 'react-icons/fa';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import { useTestIntegrationVinculateMutation } from '@partiu/api/dashboard/hooks.ts';
import { IIntegrationDataDisplayResponse } from '@partiu/api/dashboard/types.ts';

export function IntegrationTestPage() {
  const toast = useToast();
  const { id, colors, integrationId } = useTheme();
  const [cpf, setCpf] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; data?: unknown; error?: unknown } | null>(null);

  const mutation = useTestIntegrationVinculateMutation({
    onSuccess: (data) => {
      // Sucesso quando não há error nem integration_error
      const isSuccess = !data.error && !data.integration_error;

      setTestResult({ success: true, data });
      toast({
        title: isSuccess ? 'Teste realizado com sucesso!' : 'Teste concluído',
        description: isSuccess
          ? 'A integração foi testada com sucesso.'
          : 'O teste foi concluído, mas houve problemas. Verifique os detalhes abaixo.',
        status: isSuccess ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: unknown) => {
      const errorData = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = errorData?.response?.data?.message || errorData?.message || 'Erro desconhecido';
      setTestResult({
        success: false,
        error: errorData?.response?.data || errorMessage
      });
      toast({
        title: 'Erro ao testar integração',
        description: errorMessage || 'Não foi possível testar a integração. Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleTestIntegration = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!cpf || cpf.replace(/\D/g, '').length !== 11) {
      toast({
        title: 'CPF inválido',
        description: 'Por favor, informe um CPF válido.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!id) {
      toast({
        title: 'Erro',
        description: 'ID do convênio não encontrado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setTestResult(null);
    mutation.mutate({ agreementId: id, cpf });
  };

  if (!integrationId) {
    return (
      <DashboardTemplate>
        <Flex direction="column" align="center" justify="center" w="100%" minH="50vh">
          <Alert status="info" maxW="600px">
            <AlertIcon />
            <Box>
              <AlertTitle>Integração não disponível</AlertTitle>
              <AlertDescription>
                Esta funcionalidade está disponível apenas para convênios com integração configurada.
              </AlertDescription>
            </Box>
          </Alert>
        </Flex>
      </DashboardTemplate>
    );
  }

  return (
    <DashboardTemplate>
      <Flex direction="column" align="center" w="100%" p={4}>
        <Box w={{ base: '100%', md: '80%', lg: '70%' }} mb={8} textAlign="center">
          <Heading as="h1" size="xl" mb={2} color={colors.primaryColor}>
            Teste de Integração
          </Heading>
          <Text fontSize="lg" color="gray.600">
            Teste a integração do sistema "{integrationId}" informando um CPF para verificar se a vinculação está funcionando corretamente.
          </Text>
        </Box>
        <Card w={{ base: '100%', md: '80%', lg: '70%' }} borderRadius={12} boxShadow="md">
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Box display="flex" alignItems="center" gap={2}>
                <Box as={FaFlask} color={colors.primaryColor} boxSize={6} />
                <Text fontWeight="bold" fontSize="lg">
                  Testar Vinculação por CPF
                </Text>
              </Box>
              <Text color="gray.500" fontSize="sm">
                Informe um CPF para testar a integração e verificar se a vinculação está funcionando corretamente.
              </Text>
              <Box as="form" w="100%" onSubmit={handleTestIntegration}>
                <VStack spacing={4} w="100%">
                  <CPFInput
                    placeholder="CPF"
                    value={cpf}
                    onChange={(e) => setCpf(e.target.value)}
                    size="md"
                    label="CPF"
                    isDisabled={mutation.isLoading}
                  />
                  <Button
                    backgroundColor={colors.primaryColor}
                    color="white"
                    type="submit"
                    isLoading={mutation.isLoading}
                    w="100%"
                    size="md"
                    _hover={{ backgroundColor: colors.primaryColor, opacity: 0.9 }}
                    _active={{ backgroundColor: colors.primaryColor }}
                    _focus={{ boxShadow: `0 0 0 3px ${colors.primaryColor}33` }}
                  >
                    Testar Integração
                  </Button>
                </VStack>
              </Box>

              {testResult && (
                <Box mt={4}>
                  {(() => {
                    const resultData = testResult.success ? testResult.data : testResult.error;
                    const hasStructuredData = resultData && typeof resultData === 'object';
                    const structuredData = hasStructuredData ? resultData as IIntegrationDataDisplayResponse : null;

                    // Verifica se é sucesso (não tem error nem integration_error)
                    const isSuccess = structuredData && !structuredData.error && !structuredData.integration_error;

                    return (
                      <Alert
                        status={isSuccess ? 'success' : 'error'}
                        borderRadius="md"
                      >
                        <AlertIcon />
                        <Box>
                          <AlertTitle>
                            {isSuccess
                              ? 'Teste realizado com sucesso!'
                              : 'Erro no teste'}
                          </AlertTitle>
                          <AlertDescription>
                            {structuredData ? (
                              <VStack align="stretch" mt={2} spacing={3}>
                                {structuredData.status !== undefined && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="sm">Status HTTP:</Text>
                                    <Text fontSize="sm">{String(structuredData.status)}</Text>
                                  </Box>
                                )}

                                {structuredData.integration_data && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="sm" mb={2}>Dados de Integração:</Text>
                                    {structuredData.integration_data.consulta && (
                                      <Box mb={2}>
                                        <Text fontWeight="semibold" fontSize="xs" color="gray.600" mb={1}>Consulta:</Text>
                                        {Array.isArray(structuredData.integration_data.consulta) ? (
                                          <VStack align="stretch" spacing={2} fontSize="xs">
                                            {structuredData.integration_data.consulta.map((item, idx) => {
                                              // Filtra apenas os campos importantes
                                              const importantFields = {
                                                status: item.status || item.Situacao || 'N/A',
                                                data: item.data || item.data_ativacao || 'N/A',
                                                contrato: item.contrato || 'N/A',
                                                id_vd_contrato: item.id_vd_contrato || 'N/A',
                                              };

                                              return (
                                                <Box key={idx} p={3} bg="gray.50" borderRadius="md" borderLeft="3px solid" borderColor={colors.primaryColor}>
                                                  <Text><strong>Status:</strong> {String(importantFields.status)}</Text>
                                                  <Text><strong>Data:</strong> {String(importantFields.data)}</Text>
                                                  <Text><strong>Contrato:</strong> {String(importantFields.contrato)}</Text>
                                                  <Text><strong>ID Vendedor Contrato:</strong> {String(importantFields.id_vd_contrato)}</Text>
                                                </Box>
                                              );
                                            })}
                                          </VStack>
                                        ) : (
                                          <Box p={3} bg="gray.50" borderRadius="md" fontSize="xs" borderLeft="3px solid" borderColor={colors.primaryColor}>
                                            <Text><strong>Status:</strong> {String(structuredData.integration_data.consulta.status || structuredData.integration_data.consulta.Situacao || 'N/A')}</Text>
                                            <Text><strong>Data:</strong> {String(structuredData.integration_data.consulta.data || structuredData.integration_data.consulta.data_ativacao || 'N/A')}</Text>
                                            <Text><strong>Contrato:</strong> {String(structuredData.integration_data.consulta.contrato || 'N/A')}</Text>
                                            <Text><strong>ID Vendedor Contrato:</strong> {String(structuredData.integration_data.consulta.id_vd_contrato || 'N/A')}</Text>
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                    {structuredData.integration_data.contrato && structuredData.integration_data.contrato.length > 0 && (
                                      <Box>
                                        <Text fontWeight="semibold" fontSize="xs" color="gray.600" mb={1}>Contratos:</Text>
                                        <VStack align="stretch" spacing={1} fontSize="xs">
                                          {structuredData.integration_data.contrato.map((item, idx) => (
                                            <Box key={idx} p={2} bg="gray.50" borderRadius="md">
                                              {Object.entries(item).map(([key, value]) => (
                                                <Text key={key}><strong>{key}:</strong> {String(value)}</Text>
                                              ))}
                                            </Box>
                                          ))}
                                        </VStack>
                                      </Box>
                                    )}
                                  </Box>
                                )}

                                {structuredData.plan && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="sm">Plano:</Text>
                                    <Text fontSize="sm">{structuredData.plan.display_name || 'N/A'}</Text>
                                  </Box>
                                )}

                                {structuredData.plan_children && structuredData.plan_children.length > 0 && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="sm">Planos Filhos:</Text>
                                    <VStack align="stretch" spacing={1} fontSize="sm">
                                      {structuredData.plan_children.map((plan, idx) => (
                                        <Text key={idx}>• {plan.display_name || 'N/A'}</Text>
                                      ))}
                                    </VStack>
                                  </Box>
                                )}

                                {structuredData.token && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="sm">Token:</Text>
                                    <Text fontSize="xs" fontFamily="mono" wordBreak="break-all">{structuredData.token}</Text>
                                  </Box>
                                )}

                                {structuredData.message && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="sm">Mensagem:</Text>
                                    <Text fontSize="sm">{structuredData.message}</Text>
                                  </Box>
                                )}

                                {structuredData.error && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="sm" color="red.600">Erro:</Text>
                                    <Text fontSize="sm" whiteSpace="pre-wrap">{String(structuredData.error)}</Text>
                                  </Box>
                                )}

                                {structuredData.integration_error && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="sm" color="orange.600">Erro de Integração:</Text>
                                    <Text fontSize="sm" whiteSpace="pre-wrap">
                                      {Array.isArray(structuredData.integration_error)
                                        ? structuredData.integration_error.join('\n')
                                        : String(structuredData.integration_error)}
                                    </Text>
                                  </Box>
                                )}
                              </VStack>
                            ) : (
                              <Text mt={2} fontSize="sm">
                                {typeof resultData === 'object'
                                  ? JSON.stringify(resultData, null, 2)
                                  : String(resultData)}
                              </Text>
                            )}
                          </AlertDescription>
                        </Box>
                      </Alert>
                    );
                  })()}
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      </Flex>
    </DashboardTemplate>
  );
}

export default IntegrationTestPage;

